import * as $ from 'jquery';
import { BrowserEventName } from '../../gen-src/preslayer/Enums';
import { BrowserSupport } from '@tableau/browser-support';
import { CallOnDispose, DisposableHolder } from './DisposableHolder';
import { Delegate } from '../utility/Delegate';
import { DragInstance } from './DragInstance';
import { DragSourceType, IDragSource } from './IDragSource';
import { DragType } from './DragType';
import { DropFeedbackType } from './DropFeedbackType';
import { DropResult } from './DropResult';
import { EmbeddedCustomCursor, ICustomCursor } from './EmbeddedCustomCursor';
import { EventHandleSpec, NormalizedEvent } from '../EventUtils';
import { IAvatar } from './IAvatar';
import { IDisposable } from '../definition/IDisposable';
import { IDragSafeGlass, SimplifiedDragSafeGlass } from './SimplifiedDragSafeGlass';
import { IDropTarget } from './IDropTarget';
import { KeyCode } from './KeyCode';
import { Logger } from '../utility/VizclientUtils';
import { MiscUtil } from '../utility/MiscUtil';
import { TableauEventHandler } from '../TableauEventHandler';
import { TypeUtil } from '../utility/TypeUtil';
import { WindowHelper } from './WindowHelper';

// Helper functions ---------------------------
function IsNullOrEmpty(s?: string): boolean {
  return MiscUtil.IsNullOrUndefined(s) || s.length === 0;
}

// tslint:disable-next-line:no-any
function StopEvent(e: any): void {
  e.stopPropagation();
  e.preventDefault();
}

// This enum should match draginstancetype enum
enum DragInstanceType {
  Unknown = 'unknown'
}

// -------------------------

/// <summary>
/// Global manager of drag/drop process.  Responsible for recognizing drag events and working with instances of
/// <see cref="IDragSource"/> and <see cref="IDropTarget"/> to perform drag/drop operations.
/// </summary>
class DragDropManager {

  /// <summary>
  /// Attribute applied to elements to mark them as drag sources.
  /// </summary>
  private static DragSourceAttr: string = 'data-tab-drag-source';
  private static DragSourceSelector: string = '[data-tab-drag-source]';
  private static DragSourceDataKey: string = 'tabDragSource';
  private static DropTargetAttr: string = 'data-tab-drop-target';

  /// <summary>
  /// DEPRECATED: TFSID:679837 - here for backwards compat with automation.  To be removed once
  /// automation has been updated to use the ATTR instead.
  /// </summary>
  private static DropTargetClass: string = 'tabDropTarget';
  private static DropTargetSelector: string = '[data-tab-drop-target]';
  private static DropTargetDataKey: string = 'tabDropTarget';

  private static currentTarget: IDropTarget;
  private static dragAvatar: IAvatar;
  private static firstTouchDragSource: IDragSource;
  private static firstTouchDragElement: JQuery;
  private static lastDragEvent: NormalizedEvent;

  /// <summary>
  /// The last time the avatar update position was changed.
  /// </summary>
  private static lastAvatarUpdate: number;
  private static avatarUpdateInterval?: number;
  private static dragSpec: EventHandleSpec;
  private static dragDropEventHandler: TableauEventHandler;

  // Bug 154121 and TFSID 424577: WebAuthoring, Caption and Titles steal drag
  //   Use a separate event handler for drag moves since it will be attached to a Glass
  //   element that overlays the entire client area.
  private static dragGlass: IDragSafeGlass;

  private static registeredDragGlass: IDragSafeGlass[] = [];
  private static dragGlassEventHandler: TableauEventHandler;
  private static dragDisposables: DisposableHolder;

  private static currentDrag: DragInstance;

  private constructor() {
    // note: Application will call DragDropManager.Init() during startup to attach an event handler to Body
  }

  public static initialize(): void {
    DragDropManager.dragSpec = new EventHandleSpec();
    DragDropManager.dragSpec.dragStart = DragDropManager.dragStart;
    DragDropManager.dragSpec.dragMove = DragDropManager.dragMove;
    DragDropManager.dragSpec.dragEnd = DragDropManager.dragEnd;
    DragDropManager.dragSpec.pressEnd = DragDropManager.dragEnd;
    DragDropManager.dragSpec.firstTouch = DragDropManager.firstTouch;
    DragDropManager.dragDisposables = new DisposableHolder();
  }

  public static dragStarted: Delegate<(_: DragInstance) => void> = new Delegate<(_: DragInstance) => void>();
  public static add_dragStarted(ev: (_: DragInstance) => void): void {
    DragDropManager.dragStarted.add(ev);
  }
  public static remove_dragStarted(ev: (_: DragInstance) => void): void {
    DragDropManager.dragStarted.remove(ev);
  }

  public static dragMoved: Delegate<(_: DragInstance) => void> = new Delegate<(_: DragInstance) => void>();
  public static add_dragMoved(ev: (_: DragInstance) => void): void {
    DragDropManager.dragMoved.add(ev);
  }
  public static remove_dragMoved(ev: (_: DragInstance) => void): void {
    DragDropManager.dragMoved.remove(ev);
  }

  public static dragEnded: Delegate<(_: DropResult) => void> = new Delegate<(_: DropResult) => void>();
  public static add_dragEnded(ev: (_: DropResult) => void): void {
    DragDropManager.dragEnded.add(ev);
  }
  public static remove_dragEnded(ev: (_: DropResult) => void): void {
    DragDropManager.dragEnded.remove(ev);
  }

  public static currentTargetChanged: Delegate<(_: IDropTarget) => void> = new Delegate<(_: IDropTarget) => void>();
  public static add_currentTargetChanged(ev: (_: IDropTarget) => void): void {
    DragDropManager.currentTargetChanged.add(ev);
  }
  public static remove_currentTargetChanged(ev: (_: IDropTarget) => void): void {
    DragDropManager.currentTargetChanged.remove(ev);
  }

  private static dragTakeOver: (d: DragInstance, evt: NormalizedEvent) => boolean = undefined;
  public static setStartDragHandler(takeOver: (d: DragInstance, evt: NormalizedEvent) => boolean): () => void {
    if (DragDropManager.dragTakeOver === undefined) {
      DragDropManager.dragTakeOver = takeOver;
      return () => DragDropManager.unsetStartDragHandler();
    }
    // drag already taken over
    return null;
  }

  private static unsetStartDragHandler(): void {
    DragDropManager.dragTakeOver = undefined;
  }

  public static get isDragging(): boolean {
    return !MiscUtil.IsNullOrUndefined(DragDropManager.CurrentDrag);
  }

  public static get LastDragEvent(): NormalizedEvent {
    return DragDropManager.get_lastDragEvent();
  }

  public static get CurrentDrag(): DragInstance {
    return DragDropManager.get_currentDrag();
  }

  public static get CurrentTarget(): IDropTarget {
    return DragDropManager.get_currentTarget();
  }

  public static set CurrentTarget(value: IDropTarget) {
    const changed: boolean = DragDropManager.currentTarget !== value;
    DragDropManager.currentTarget = value;
    if (changed && !MiscUtil.IsNullOrUndefined(DragDropManager.currentTargetChanged)) {
      DragDropManager.currentTargetChanged.invoke(DragDropManager.currentTarget);
    }
  }

  /// <summary>
  /// Called from the Application when it's safe to acess the Document body
  /// </summary>
  public static init(): void {
    if (DragDropManager.dragDropEventHandler === undefined) {
      DragDropManager.dragDropEventHandler = new TableauEventHandler(document.body, DragDropManager.dragSpec);
    }
  }

  private static addClassSVGCompatible(targetElement: JQuery, className: string): void {
    let element = targetElement[0];
    let classNamesString = element.getAttribute('class');
    if (IsNullOrEmpty(classNamesString)) {
      element.setAttribute('class', className);
      return;
    }
    let classNamesArray = classNamesString.split(' ');
    if (classNamesArray.indexOf(className) === -1) {
      element.setAttribute('class', classNamesArray.concat(className).join(' '));
    }
  }

  private static removeClassSVGCompatible(targetElement: JQuery, className: string): void {
    let element = targetElement[0];
    let classNamesString = element.getAttribute('class');
    if (IsNullOrEmpty(classNamesString)) {
      return;
    }
    let classNamesArray = classNamesString.split(' ');
    let classNameIndex = classNamesArray.indexOf(className);
    if (classNameIndex >= 0) {
      classNamesArray.splice(classNameIndex, 1);
      element.setAttribute('class', classNamesArray.join(' '));
    }
  }

  public static attachDropTarget(targetElement: JQuery, dropTarget: IDropTarget): void {
    targetElement.attr(DragDropManager.DropTargetAttr, '1');
    DragDropManager.addClassSVGCompatible(targetElement, DragDropManager.DropTargetClass);
    targetElement.data(DragDropManager.DropTargetDataKey, dropTarget);
  }

  public static detachDropTarget(targetElement: JQuery): void {
    targetElement.removeAttr(DragDropManager.DropTargetAttr);
    targetElement.removeData(DragDropManager.DropTargetDataKey);
    DragDropManager.removeClassSVGCompatible(targetElement, DragDropManager.DropTargetClass);
  }

  public static isDropTarget(targetElement: JQuery): boolean {
    return !MiscUtil.IsNullOrUndefined(targetElement.attr(DragDropManager.DropTargetAttr));
  }

  public static attachDragSource(sourceElement: JQuery, dragSource: IDragSource): void {
    sourceElement.attr(DragDropManager.DragSourceAttr, '1');
    sourceElement.data(DragDropManager.DragSourceDataKey, dragSource);
  }

  public static removeDragSource(sourceElement: JQuery): void {
    sourceElement.removeData(DragDropManager.DragSourceDataKey);
    sourceElement.removeAttr(DragDropManager.DragSourceAttr);
  }

  public static isDragSource(sourceElement: JQuery): boolean {
    return !MiscUtil.IsNullOrUndefined(sourceElement.attr(DragDropManager.DragSourceAttr));
  }

  private static createDragGlass(): void {
    DragDropManager.dragGlass = new SimplifiedDragSafeGlass();
    DragDropManager.dragGlass.show();
    if (DragDropManager.CurrentDrag.get_hasPayload()
      && DragDropManager.CurrentDrag.get_dragType() === DragType.dragmove) {
      DragDropManager.setDragCursor(EmbeddedCustomCursor.copyFromElement(DragDropManager.firstTouchDragElement));
    }
    if (!MiscUtil.IsNullOrUndefined(DragDropManager.dragGlass.Element)) {
      DragDropManager.dragGlassEventHandler =
        new TableauEventHandler(DragDropManager.dragGlass.Element[0], DragDropManager.dragSpec);
      DragDropManager.dragDisposables = new DisposableHolder();
      DragDropManager.dragDisposables.add(DragDropManager.dragGlass);
      DragDropManager.dragDisposables.add(DragDropManager.dragGlassEventHandler);
      DragDropManager.dragDisposables.add(
        DragDropManager.BindWithDispose($(document.body), BrowserEventName.KeyDown, DragDropManager.handleKeyDown));
      DragDropManager.dragDisposables.add(
        DragDropManager.BindWithDispose($(document.body), BrowserEventName.KeyUp, DragDropManager.handleKeyUp));
    }
  }

  private static destroyDragGlass(): void {
    DragDropManager.dragDisposables.dispose();
    DragDropManager.dragDisposables = new DisposableHolder();
  }

  public static startDrag(draggedObject: DragInstance, e: NormalizedEvent, optionalDragElement: JQuery = null): void {
    if (MiscUtil.IsNullOrUndefined(draggedObject) || MiscUtil.IsNullOrUndefined(e) || !draggedObject.get_hasPayload()) {
      return;
    }

    if (!MiscUtil.IsNullOrUndefined(optionalDragElement)) {
      DragDropManager.firstTouchDragElement = optionalDragElement;
    }
    DragDropManager.lastDragEvent = e;
    DragDropManager.cancelDrag();
    DragDropManager.currentDrag = draggedObject;
    DragDropManager.setDragAvatar(DragDropManager.CurrentDrag.get_dragAvatar());
    DragDropManager.updateAvatarPosition();
    DragDropManager.avatarUpdateInterval = WindowHelper.RequestAnimationFrame(DragDropManager.updateAvatarPosition);
    DragDropManager.createDragGlass();
    DragDropManager.setDragTarget(e);

    // check if somebody wants to take over the drag process (dragdrop contract on desktop for example)
    if (DragDropManager.dragTakeOver !== undefined) {
      if (DragDropManager.dragTakeOver(draggedObject, e)) {
        Logger.Debug('Drag started but taken over');
        DragDropManager.cancelDrag();
        return;
      }
    }

    Logger.Debug(`Drag started ${DragDropManager.CurrentDrag}`);
    if (!MiscUtil.IsNullOrUndefined(DragDropManager.dragStarted)) {
      DragDropManager.dragStarted.invoke(DragDropManager.CurrentDrag);
    }
  }

  public static cancelDrag(drag: DragInstance = null): void {
    if (!DragDropManager.isDragging) {
      return;
    }
    if (MiscUtil.IsNullOrUndefined(drag) || drag === DragDropManager.CurrentDrag) {
      Logger.Debug('Drag cancelled');
      DragDropManager.CurrentDrag.cancelDrag();
      DragDropManager.endDrag(
        null/*target:*/,
        true/*cancelled:*/,
        false/*ctrlKeyPressed:*/,
        () => $.Deferred<boolean>().resolve(false)/*dropAction:*/
      );
    }
  }

  public static drop(target: IDropTarget, isCopyDrop: boolean = false, isShiftDrop: boolean = false, isRightDrop: boolean = false): void {
    DragDropManager.currentDrag.isCopyDrag = isCopyDrop;
    DragDropManager.currentDrag.isShiftDrag = isShiftDrop;
    DragDropManager.currentDrag.isRightDrag = isRightDrop;
    DragDropManager.endDrag(
      target,
      false /*cancelled:*/,
      DragDropManager.CurrentDrag.isCopyDrag /*ctrlKeyPressed:*/,
      () => { /*dropAction:*/
        if (!MiscUtil.IsNullOrUndefined(target)) {
          return target.acceptDrop(DragDropManager.CurrentDrag).always(success => {
            Logger.Debug(`Drop: ${success} ${target}`);
          });
        } else {
          Logger.Debug('Drop with no target');
          DragDropManager.CurrentDrag.dropToNowhereAction();
          return $.Deferred<boolean>().resolve(true);
        }
      });
  }

  public static firstTouch(e: NormalizedEvent): void {
    let src: JQuery = $(e.target).closest(DragDropManager.DragSourceSelector);
    if (MiscUtil.IsNullOrUndefined(src) || src.length === 0) {
      DragDropManager.firstTouchDragSource = null;
      DragDropManager.firstTouchDragElement = null;
      return;
    }
    DragDropManager.firstTouchDragSource = src.data(DragDropManager.DragSourceDataKey) as IDragSource;
    DragDropManager.firstTouchDragElement = src;
  }

  public static dragEnd(e: NormalizedEvent): void {
    if (MiscUtil.IsNullOrUndefined(DragDropManager.CurrentDrag)) {
      return;
    }
    switch (DragDropManager.CurrentDrag.get_dragType()) {
      case DragType.dragmove:
        DragDropManager.endDrag(
          null /*target:*/,
          false /*cancelled:*/,
          false /*ctrlKeyPressed:*/,
          () => { /*dropAction:*/
            DragDropManager.CurrentDrag.endDragAt(e);
            return $.Deferred<boolean>().resolve(true);
          });
        break;
      case DragType.dragmovedrop:
      case DragType.dragdrop:
        DragDropManager.drop(DragDropManager.CurrentTarget, e.IsCtrlKey, e.shiftKey, e.IsRightMouseEvent);
        break;
      default:
        break;
    }
  }

  public static dragMove(e: NormalizedEvent): void {
    if (!DragDropManager.isDragging) {
      return;
    }
    StopEvent(e);
    DragDropManager.lastDragEvent = e;
    DragDropManager.currentDrag.isCopyDrag = e.IsCtrlKey;
    DragDropManager.currentDrag.isShiftDrag = e.shiftKey;
    WindowHelper.ClearSelection();
    const now: number = Date.now();
    if ((now - DragDropManager.lastAvatarUpdate) > 75) {
      if (!MiscUtil.IsNullOrUndefined(DragDropManager.avatarUpdateInterval)) {
        WindowHelper.CancelAnimationFrame(DragDropManager.avatarUpdateInterval);
      }
      DragDropManager.updateAvatarPosition();
    }
    DragDropManager.setDragTarget(e);
    if (!MiscUtil.IsNullOrUndefined(DragDropManager.dragMoved)) {
      DragDropManager.dragMoved.invoke(DragDropManager.CurrentDrag);
    }
  }
  private static handleKeyDown(e: JQuery.Event): void {
    if (!DragDropManager.isDragging) {
      return;
    }
    StopEvent(e);
    switch (e.which) {
      case KeyCode.Shift:
        DragDropManager.lastDragEvent.shiftKey = true;
        DragDropManager.dragMove(DragDropManager.LastDragEvent);
        break;
      case KeyCode.Ctrl:
        DragDropManager.lastDragEvent.ctrlKey = true;
        DragDropManager.dragMove(DragDropManager.LastDragEvent);
        break;
      case KeyCode.Meta:
        DragDropManager.lastDragEvent.metaKey = true;
        DragDropManager.dragMove(DragDropManager.LastDragEvent);
        break;
      default:
        break;
    }
  }

  private static handleKeyUp(e: JQuery.Event): void {
    if (!DragDropManager.isDragging) {
      return;
    }
    StopEvent(e);
    switch (e.which) {
      case KeyCode.Shift:
        DragDropManager.lastDragEvent.shiftKey = false;
        DragDropManager.dragMove(DragDropManager.LastDragEvent);
        break;
      case KeyCode.Ctrl:
        DragDropManager.lastDragEvent.ctrlKey = false;
        DragDropManager.dragMove(DragDropManager.LastDragEvent);
        break;
      case KeyCode.Meta:
        DragDropManager.lastDragEvent.metaKey = false;
        DragDropManager.dragMove(DragDropManager.LastDragEvent);
        break;
      case KeyCode.Escape:
        DragDropManager.cancelDrag(DragDropManager.CurrentDrag);
        break;
      default:
        break;
    }
  }

  private static setDragTarget(e: NormalizedEvent): void {
    switch (DragDropManager.CurrentDrag.get_dragType()) {
      case DragType.dragmove:
        DragDropManager.CurrentDrag.moveTo(e);
        break;
      case DragType.dragdrop:
        DragDropManager.setTargetForDrop(e);
        break;
      case DragType.dragmovedrop:
        DragDropManager.CurrentDrag.moveTo(e);
        DragDropManager.setTargetForDrop(e);
        break;
      default:
        break;
    }
  }

  private static setTargetForDrop(e: NormalizedEvent): void {
    let target: IDropTarget = DragDropManager.findDropTarget(e);
    if (!MiscUtil.IsNullOrUndefined(DragDropManager.CurrentTarget)) {
      if (DragDropManager.CurrentTarget !== target) {
        DragDropManager.CurrentTarget.dragExit(DragDropManager.CurrentDrag);
      } else if (DragDropManager.CurrentTarget.get_feedbackType() === DropFeedbackType.dropfeedbackuponentry) {
        Logger.Debug(`Drag move: target=${DragDropManager.CurrentTarget}`);
        return;
      }
    }
    if (!MiscUtil.IsNullOrUndefined(target)) {
      let newTarget: IDropTarget = target.dragOver(DragDropManager.CurrentDrag);
      while (!MiscUtil.IsNullOrUndefined(newTarget) && newTarget !== target) {
        target = newTarget;
        newTarget = target.dragOver(DragDropManager.CurrentDrag);
      }
      target = newTarget;
    }
    if (!MiscUtil.IsNullOrUndefined(target)) {
      DragDropManager.setDragCursor(target.getDropCursor(DragDropManager.CurrentDrag));
    } else {
      Logger.Debug('No target');
      DragDropManager.setDragCursor(DragDropManager.CurrentDrag.get_defaultDropCursor());
    }
    DragDropManager.CurrentTarget = target;
  }

  public static setDragCursor(c: ICustomCursor): void {
    if (!MiscUtil.IsNullOrUndefined(DragDropManager.dragGlass)
      && !MiscUtil.IsNullOrUndefined(DragDropManager.dragGlass.Element)) {
      c.setCursor(DragDropManager.dragGlass.Element);
    }
  }

  public static dragStart(e: NormalizedEvent): void {
    if (DragDropManager.isDragging) {
      return;
    }

    let ds: IDragSource = DragDropManager.firstTouchDragSource;
    if (MiscUtil.IsNullOrUndefined(ds)) {
      const src: JQuery = $(e.target).closest(DragDropManager.DragSourceSelector);
      ds = src.data(DragDropManager.DragSourceDataKey) as IDragSource;
    }
    if (MiscUtil.IsNullOrUndefined(ds)) {
      return;
    }

    // Make sure the drag source can accept this drag type.
    if (ds.acceptsDragType() === DragSourceType.LeftMouse && e.IsMouseEvent && !e.IsLeftMouseEvent) {
      return;
    }

    let drag: DragInstance = ds.startDrag(e);
    if (MiscUtil.IsNullOrUndefined(drag)) {
      return;
    }
    DragDropManager.startDrag(drag, e);
  }

  private static endDrag(
    target: IDropTarget,
    cancelled: boolean,
    ctrlKeyPressed: boolean,
    dropAction: () => JQuery.Deferred<boolean>): void {
    DragDropManager.setDragAvatar(null);
    DragDropManager.destroyDragGlass();
    if (!MiscUtil.IsNullOrUndefined(DragDropManager.avatarUpdateInterval)) {
      WindowHelper.CancelAnimationFrame(DragDropManager.avatarUpdateInterval);
      DragDropManager.avatarUpdateInterval = null;
    }
    dropAction().always((success: boolean) => {
      if (!MiscUtil.IsNullOrUndefined(DragDropManager.dragEnded)) {
        DragDropManager.dragEnded.invoke(
          new DropResult(
            DragDropManager.CurrentDrag,
            target,
            success,
            cancelled,
            ctrlKeyPressed));
      }
      DragDropManager.currentDrag = null;
      DragDropManager.CurrentTarget = null;
    });
  }

  private static setDragAvatar(newAvatar: IAvatar): void {
    if (!MiscUtil.IsNullOrUndefined(DragDropManager.dragAvatar)) {
      DragDropManager.dragAvatar.get_rootElement().detach();
      let d: IDisposable = DragDropManager.dragAvatar as IDisposable;
      d.dispose();
      DragDropManager.dragAvatar = null;
    }
    DragDropManager.dragAvatar = newAvatar;
    if (!MiscUtil.IsNullOrUndefined(DragDropManager.dragAvatar)) {
      DragDropManager.dragAvatar.get_rootElement().addClass('dragAvatar').css('position', 'absolute').css('pointer-events', 'none');
      DragDropManager.dragAvatar.addToDom($('body'));
    }
  }

  private static updateAvatarPosition(): void {
    if (MiscUtil.IsNullOrUndefined(DragDropManager.dragAvatar)) {
      return;
    }

    let computeCoord: (_: number, __: number, ___: number) => number
      = (totalDimension: number, dragEventPos: number, avatarDimension: number) => {
        if (dragEventPos < (avatarDimension / 2)) {
          return 0;
        } else if (dragEventPos > (totalDimension - (avatarDimension / 2))) {
          return totalDimension - avatarDimension;
        } else {
          return dragEventPos - (avatarDimension / 2);
        }
      };

    const body: Element = DragDropManager.documentBodyOverride === undefined ? document.body : DragDropManager.documentBodyOverride();
    DragDropManager.setElementPosition(
      DragDropManager.dragAvatar.get_rootElement(),
      computeCoord(
        body.scrollWidth,
        DragDropManager.LastDragEvent.pageX,
        Math.round(DragDropManager.dragAvatar.get_rootElement().outerWidth())),
      computeCoord(
        body.scrollHeight,
        DragDropManager.LastDragEvent.pageY,
        Math.round(DragDropManager.dragAvatar.get_rootElement().outerHeight())));

    DragDropManager.lastAvatarUpdate = Date.now();
    DragDropManager.avatarUpdateInterval = WindowHelper.RequestAnimationFrame(DragDropManager.updateAvatarPosition);
  }

  public static getElementFromPointInternal(e: NormalizedEvent): JQuery {
    DragDropManager.disableHitTestsOnDragGlass();
    let x: number = e.clientX;
    let y: number = e.clientY;
    if (BrowserSupport.UseAlternateHitStrategy()) {
      if (!MiscUtil.IsNullOrUndefined(e.touches) && e.touches.length > 0 && !MiscUtil.IsNullOrUndefined(e.touches[0].screenX)) {
        x = e.touches[0].screenX;
        y = e.touches[0].screenY as number;
      } else {
        x = TypeUtil.GetField<number>(e.originalEvent, 'screenX');
        y = TypeUtil.GetField<number>(e.originalEvent, 'screenY');
      }
    }
    let overNode: JQuery = $(null);
    if (!MiscUtil.IsNullOrUndefined(x) && !MiscUtil.IsNullOrUndefined(y)) {
      overNode = $(document.elementFromPoint(x, y));
    }
    DragDropManager.enableHitTestsOnDragGlass();
    return overNode;
  }

  public static getElementFromPoint(e: NormalizedEvent): JQuery {
    return DragDropManager.getElementFromPointInternal(e);
  }

  private static disableHitTestsOnDragGlass(): void {
    DragDropManager.registeredDragGlass.forEach(g => {
      g.disableHitTests();
    });
  }

  private static enableHitTestsOnDragGlass(): void {
    DragDropManager.registeredDragGlass.forEach(g => {
      g.enableHitTests();
    });
  }

  public static findDropTarget(e: NormalizedEvent): IDropTarget | null {
    if (!DragDropManager.isDragging) {
      return null;
    }
    const dragInstance = DragDropManager.CurrentDrag;
    const targets = DragDropManager.getPotentialDropTargets(e);
    for (let i = 0; i < targets.length; i++) {
      const hit = targets[i];
      const hitTarget = hit.data(DragDropManager.DropTargetDataKey) as IDropTarget;
      if (DragDropManager.canAccept(hitTarget, dragInstance)) {
        return hitTarget.getDropTarget(hit);
      }
    }
    return null;
  }

  private static getPotentialDropTargets(e: NormalizedEvent): JQuery[] {
    let targets: JQuery[] = [];
    let dropTarget = DragDropManager.getElementFromPoint(e).closest(DragDropManager.DropTargetSelector);
    // In order to handle edge cases, search the next pixel to the left if no drop targets are found. This helps mitigate issues where
    // Document.ElementFromPoint() has an off-by-1 error resulting in an element farther up the DOM hierarchy being chosen, which will
    // not find the right drop target.
    if (dropTarget.length === 0) {
      --e.clientX;
      dropTarget = DragDropManager.getElementFromPoint(e).closest(DragDropManager.DropTargetSelector);
    }
    if (dropTarget.length > 0) {
      targets.push(dropTarget);
      dropTarget.parents(DragDropManager.DropTargetSelector).each((unusedindex, element) => {
        targets.push($(element));
      });
    }
    return targets;
  }

  private static canAccept(dropTarget: IDropTarget, dragInstance: DragInstance): boolean {
    return !MiscUtil.IsNullOrUndefined(dropTarget)
      && !MiscUtil.IsNullOrUndefined(dragInstance)
      && !MiscUtil.IsNullOrUndefined(dropTarget.dragOver(dragInstance));
  }

  public static registerDragGlass(g: IDragSafeGlass): void {
    DragDropManager.registeredDragGlass.push(g);
  }

  public static unregisterDragGlass(g: IDragSafeGlass): void {
    DragDropManager.registeredDragGlass = DragDropManager.registeredDragGlass.filter(glass => glass !== g);
  }

  private static eventCounter: number = 0;

  // <summary>
  /// Binds an event handler to a jQuery object and returns a disposable for unbinding the same event.
  /// </summary>
  /// <see cref="jQueryObject.On(string,jQueryApi.jQueryEventHandler)"/>
  /// <param name="element">The element(s) to bind to</param>
  /// <param name="eventName">The event name</param>
  /// <param name="handler">The event handler</param>
  /// <returns>A disposable that when called will unbind bound event handler</returns>
  // tslint:disable-next-line:no-any
  private static BindWithDispose(element: JQuery, eventName: BrowserEventName, handler: (e: any) => void): IDisposable {
    const eventId: string = `${eventName}.${++DragDropManager.eventCounter}`;
    element.on(eventId, handler);
    return new CallOnDispose(() => { element.off(eventId); });
  }

  /// <summary>
  /// Sets an elements position using either CSS transform or absolute positioning, depending on what
  /// is supported.  Assumes element is added to body.
  /// </summary>
  public static setElementPosition(e: JQuery, pageX: number, pageY: number, duration: string = null, useTransform: boolean = true): void {
    if (useTransform) {
      let styling: JQuery.PlainObject<string> = {
        top: '0px',
        left: '0px'
      };
      let transformVal: string = `translate3d(${pageX}px, ${pageY}px, 0px)`;
      styling[BrowserSupport.CssTransformName()] = transformVal;
      if (!MiscUtil.IsNullOrUndefined(duration)) {
        styling[BrowserSupport.CssTransitionName() + '-duration'] = duration;
      }
      e.css(styling);
    } else {
      let css: JQuery.PlainObject<string> = {
        position: 'absolute',
        top: `${pageY}px`,
        left: `${pageX}px`
      };
      css[BrowserSupport.CssTransformName()] = '';
      e.css(css);
    }
  }

  // Redirect helper functions for unittests ----------------------------------------------

  // Test hook
  public static documentBodyOverride: () => HTMLElement = undefined;

  // Redirection for saltarelle generated javascript getters.
  // Stringly typed jasmine unittests spy on these functions with the specific names.
  // TODO: These could be removed once the saltarelle unittests do not depend on them.
  public static get_isDragging(): boolean { return DragDropManager.isDragging; }
  public static get_lastDragEvent(): NormalizedEvent { return DragDropManager.lastDragEvent; }
  public static get_currentTarget(): IDropTarget { return DragDropManager.currentTarget; }
  public static get_currentDrag(): DragInstance { return DragDropManager.currentDrag; }

  // These are shortcuts for jasmine unittests to trigger the events directly.
  public static triggerEventCurrentTargetChanged(d: IDropTarget): void {
    DragDropManager.currentTargetChanged.invoke(d);
  }

  public static triggerEventDragEnded(d: DropResult): void {
    DragDropManager.dragEnded.invoke(d);
  }

  public static isPresModelDragInstance(d: DragInstance): boolean {
    return !MiscUtil.IsNullOrUndefined(d)
      && d.get_hasPayload()
      && d.presmodelType !== undefined
      && d.presmodelType !== ''
      && d.presmodelType !== DragInstanceType.Unknown;
  }
}

// call static initializer
DragDropManager.initialize();

export { IDragSafeGlass, DragDropManager };
